import React, { useRef } from 'react';
import Button from '@components/atoms/button/button';
import { useTranslation } from 'next-i18next';
import { CartContext } from '@context/cart.context';
import { useAnalytics } from '@context/analytics/analytics.context';
import { useContextSelector } from 'use-context-selector';
import { useBucket } from '@context/bucket.context';
import dynamic from 'next/dynamic';
import Icon from '@components/atoms/icon/icon';
import Link from '@components/atoms/link/link';
import styles from './cart.module.scss';

const FloatingCart = dynamic(() => import('@components/atoms/cart/floating-cart/floating-cart'), {
  ssr: false,
});

const PreviewBox = dynamic(() => import('@components/atoms/preview-box/preview-box'), {
  ssr: false,
});

const Cart: React.FC = () => {
  const { t } = useTranslation('default-layout');
  const { t: tA11y } = useTranslation('a11y');
  const { sendEvent } = useAnalytics();
  const { isFeatureEnabled } = useBucket();
  const productsInCart = useContextSelector(CartContext, (v) => v.productsInCart);
  const checkoutUrl = useContextSelector(CartContext, (v) => v.checkoutUrl);
  // Not sending LaunchDarkly tracking here because this component is rendered on every page and the flag exposure is not relevant to track.
  const isRemoveMiniBagOn = isFeatureEnabled((f) => f.conex4134RemoveMiniBag, true) ?? false;

  const isCartPreviewOpened = useContextSelector(CartContext, (v) => v.isCartPreviewOpened);
  const setIsCartPreviewOpened = useContextSelector(CartContext, (v) => v.setIsCartPreviewOpened);

  const cartRef = useRef<HTMLDivElement>(null);

  const countLabel = productsInCart.length > 99 ? '99+' : productsInCart.length;

  const closePreviewBox = (): void => {
    setIsCartPreviewOpened(false);
  };

  const cartActionTracking = (): void => {
    sendEvent(
      {
        type: 'event',
        payload: {
          event: 'headerClick',
          button_name: 'cart',
        },
      },
      {
        type: 'event',
        payload: {
          event: 'navigation_header',
          category: 'navigation',
          action: 'header',
          label: 'cart',
          value: '6',
        },
      },
    );
  };

  const cartAction = (): void => {
    setIsCartPreviewOpened(!isCartPreviewOpened);
    cartActionTracking();
  };

  return (
    <div
      className={styles.cart}
      ref={cartRef}
    >
      {/* Screen reader announcement for cart count changes */}
      <div
        role="status"
        aria-atomic="true"
        aria-live="polite"
        className="visuallyhidden"
      >
        {productsInCart.length > 0 &&
          tA11y(productsInCart.length === 1 ? 'A11Y.ICON.BAG_SINGULAR' : 'A11Y.ICON.BAG_PLURAL', {
            productsInCart: countLabel,
          })}
      </div>

      {isRemoveMiniBagOn ? (
        <Link
          href={checkoutUrl}
          data-cy="header_cart_button"
          className={styles.cart__link}
          onClick={() => cartActionTracking()}
          aria-label={
            productsInCart.length === 0
              ? tA11y('A11Y.ICON.BAG_NO_ITEMS')
              : tA11y(productsInCart.length === 1 ? 'A11Y.ICON.BAG_SINGULAR' : 'A11Y.ICON.BAG_PLURAL', {
                  productsInCart: countLabel,
                })
          }
        >
          <Icon
            className={styles.cart__cartIcon}
            name="bag"
            ariaHidden
          />
          {/* Show the amount of items in the cart if there are any */}
          {productsInCart.length > 0 && <span className={styles.cart__count}>{countLabel}</span>}
        </Link>
      ) : (
        <Button
          aria-label={
            productsInCart.length === 0
              ? tA11y('A11Y.ICON.BAG_NO_ITEMS')
              : tA11y(productsInCart.length === 1 ? 'A11Y.ICON.BAG_SINGULAR' : 'A11Y.ICON.BAG_PLURAL', {
                  productsInCart: countLabel,
                })
          }
          aria-haspopup="true"
          aria-expanded={isCartPreviewOpened}
          disableDefaultStyling
          onClick={cartAction}
          data-cy="floating_cart_button"
        >
          <Icon
            className={styles.cart__cartIcon}
            name="bag"
            ariaHidden
          />
          {/* Show the amount of items in the cart if there are any */}
          {productsInCart.length > 0 && <span className={styles.cart__count}>{countLabel}</span>}
        </Button>
      )}
      {/* Opens a popup with a list of items in the cart */}
      {isCartPreviewOpened && (
        <PreviewBox
          isActive={isCartPreviewOpened}
          title={t('HEADER.NAVIGATION.CART_TITLE')}
          closePreviewBox={closePreviewBox}
          outsideClickRef={cartRef}
          isCart={true}
        >
          <div className={styles.cart__previewBoxContent}>
            <FloatingCart />
          </div>
        </PreviewBox>
      )}
    </div>
  );
};

export default Cart;
